// @ts-nocheck
/*
 * Port of views/dashboard/agent-call-stats/index.js.
 * API: API_AGENT_CALL_STATS (start_date = end_date = today IST).
 *   - Today only (2026-10-30): date range + 5 period summary cards removed;
 *     the summary strip comes from the same response's `totals`.
 *   - Server paged (2026-10-30, `page` opt-in): backend returns only agents
 *     with activity today, sorted by total desc, searched by name / id.
 *     Agent dropdown (full agent list) replaced by that search.
 *   - Bar chart = the current page's agents. "Overall Distribution" donut
 *     removed: it repeated the summary strip.
 * Outcomes are Answered / Failed / Abandoned only (backend callOutcome).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import Chart from 'react-apexcharts';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_CALL_STATS } from '@/service/url';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/data-table/DataTable';
// NOT IN USE: dashboard Call Quality panel hidden (2026-10-30). The
// Call Quality report page still renders it.
// import CallQualityMetricsPanel from '@/features/call-quality/CallQualityMetricsPanel';
import { SUCCESS_COLOR, WARNING_COLOR, FAILURE_COLOR } from '../chart-palette';

const pad = (n) => String(n).padStart(2, '0');
// YYYY-MM-DD in IST, same day boundary the backend uses (moment-tz Asia/Kolkata).
const todayIST = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const formatDuration = (totalSeconds) => {
  const s = Number(totalSeconds) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${pad(m)}m ${pad(sec)}s`;
  return `${m}m ${pad(sec)}s`;
};

const fmtPct = (v) => `${Number(v || 0).toFixed(2)}%`;

function CountPct({ count, pctValue }) {
  return (
    <span>
      {count ?? 0} <span className="text-xs text-muted-foreground">({fmtPct(pctValue)})</span>
    </span>
  );
}

function Stat({ label, value, sub, className = '' }) {
  return (
    <div className="min-w-[110px] rounded-md border border-border bg-card px-3 py-2">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold leading-tight ${className}`}>{value}</div>
      {sub ? <div className="text-[11px] text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

export default function AgentCallStats() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [paging, setPaging] = useState({ pageIndex: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [activeAgents, setActiveAgents] = useState(0);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    let mounted = true;
    setTableLoading(true);
    setError('');
    const day = todayIST();
    const params = new URLSearchParams({
      start_date: day,
      end_date: day,
      page: String(paging.pageIndex),
      limit: String(paging.pageSize),
    });
    if (search) params.append('search', search);

    getApiCall(`${API_AGENT_CALL_STATS}?${params.toString()}`)
      .then((resp) => {
        if (!mounted) return;
        const list = Array.isArray(resp?.agents) ? resp.agents : [];
        setRows(list);
        setTotals(resp?.totals || null);
        setRowCount(Number(resp?.total_rows ?? list.length) || 0);
        setActiveAgents(Number(resp?.active_agents ?? list.length) || 0);
      })
      .catch((err) => {
        if (!mounted) return;
        setRows([]);
        setTotals(null);
        setRowCount(0);
        setError(typeof err === 'string' ? err : 'Failed to load agent call stats');
      })
      .finally(() => mounted && setTableLoading(false));
    return () => { mounted = false; };
  }, [paging, search]);

  useEffect(() => load(), [load]);

  // Debounced server search; a new term starts again at page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next === search) return;
      setSearch(next);
      setPaging((p) => ({ ...p, pageIndex: 0 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput, search]);

  const barChart = useMemo(() => {
    const top = rows; // current page, already sorted by total desc server-side
    return {
      options: {
        chart: { type: 'bar', stacked: true, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: false, borderRadius: 4, columnWidth: '55%' } },
        xaxis: { categories: top.map((r) => r.agent_name || `#${r.agent_id}`) },
        legend: { position: 'top' },
        colors: [SUCCESS_COLOR, FAILURE_COLOR, WARNING_COLOR],
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false },
      },
      series: [
        { name: 'Answered', data: top.map((r) => r.answered) },
        { name: 'Failed', data: top.map((r) => r.failed) },
        { name: 'Abandoned', data: top.map((r) => r.abandoned) },
      ],
    };
  }, [rows]);


  // Compact table: count + % share one cell, total/avg wrap share one cell.
  const perAgentColumns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'agent_name', header: 'Agent', enableSorting: false },
      { accessorKey: 'total', header: 'Total', enableSorting: false },
      {
        accessorKey: 'answered',
        enableSorting: false,
        header: 'Answered',
        cell: ({ row }) => <CountPct count={row.original.answered} pctValue={row.original.answered_pct} />,
      },
      {
        accessorKey: 'failed',
        enableSorting: false,
        header: 'Failed',
        cell: ({ row }) => <CountPct count={row.original.failed} pctValue={row.original.failed_pct} />,
      },
      {
        accessorKey: 'abandoned',
        enableSorting: false,
        header: 'Abandoned',
        cell: ({ row }) => <CountPct count={row.original.abandoned} pctValue={row.original.abandoned_pct} />,
      },
      {
        accessorKey: 'total_wrap_seconds',
        enableSorting: false,
        header: 'Wrap',
        cell: ({ row }) => (
          <div className="text-xs leading-tight">
            <div><span className="text-muted-foreground">Total </span>{formatDuration(row.original.total_wrap_seconds)}</div>
            <div><span className="text-muted-foreground">Avg </span>{formatDuration(row.original.avg_wrap_seconds)}</div>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="mb-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h4 className="text-base font-bold text-foreground">Agent Call Statistics</h4>
          <span className="rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Today</span>
        </div>
        <div className="flex items-center gap-2">
          <Input
            aria-label="Search agent"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search agent name or ID..."
            maxLength={100}
            className="h-8 w-[220px] text-sm"
          />
          <button
            type="button"
            onClick={load}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Refresh Agent Call Stats"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Today summary strip (replaces the 5 period cards) */}
      <div className="mb-3 flex flex-wrap gap-2">
        <Stat label="Total" value={totals?.total ?? 0} />
        <Stat label="Answered" value={totals?.answered ?? 0} sub={fmtPct(totals?.answered_pct)} className="text-emerald-600" />
        <Stat label="Failed" value={totals?.failed ?? 0} sub={fmtPct(totals?.failed_pct)} className="text-red-500" />
        <Stat label="Abandoned" value={totals?.abandoned ?? 0} sub={fmtPct(totals?.abandoned_pct)} className="text-amber-600" />
        <Stat label="Total Wrap" value={formatDuration(totals?.total_wrap_seconds)} />
        <Stat label="Active Agents" value={activeAgents} />
      </div>

      {/* Charts */}
      {/* Bar chart only when there is something to plot (current page's agents). */}
      {rows.some((r) => r.total > 0) ? (
        <Card className="mb-3 p-3">
          <h5 className="mb-1 text-sm font-bold text-foreground">Answered / Failed / Abandoned per Agent <span className="text-xs font-normal text-muted-foreground">(this page)</span></h5>
          <Chart options={barChart.options} series={barChart.series} type="bar" height={220} />
        </Card>
      ) : null}
      {/*
       * NOT IN USE: "Overall Distribution" donut removed (2026-10-30) - it
       * repeated the Answered / Failed / Abandoned % in the summary strip.
       */}

      <Card className="p-3">
        <h5 className="mb-2 text-sm font-bold text-foreground">Per-Agent Breakdown</h5>
        {error ? <div className="mb-2 text-sm text-destructive">{error}</div> : null}
        <DataTable
          tableId="dashboard.agent-call-stats.per-agent"
          columns={perAgentColumns}
          data={rows}
          isLoading={tableLoading}
          emptyTitle={search ? "No matching agent" : "No agent activity today"}
          emptyDescription={search ? "No agent with calls today matches this search." : "Agents appear here once they make, take or wrap up a call today."}
          pageSize={paging.pageSize}
          disableSearch
          exportFilename="agent-call-stats-today.csv"
          serverPagination={{ ...paging, rowCount, onChange: setPaging }}
        />
      </Card>

      {/*
       * NOT IN USE: WebRTC Call Quality panel hidden from the dashboard at
       * user request (2026-10-30). Still available on the Call Quality
       * report page. To restore, uncomment this and the import above.
       *
       * <div className="mt-3">
       *   <CallQualityMetricsPanel agentId={null} from={day} to={`${day} 23:59:59`} />
       * </div>
       */}
    </div>
  );
}
